import { logError } from 'setup/logger/logError';
import { logInfo } from 'setup/logger/logInfo';
import { requestServiceWorkerUpdate } from '../requestServiceWorkerUpdate';
import { collectServiceWorkerDiagnostics } from './collectServiceWorkerDiagnostics';
import { LogLevel } from 'shared/types/logger';
import {
  classifyServiceWorkerFailure,
  isFailureKindLoggedOnce,
  isOutdatedWorkerFailure,
  serviceWorkerFailureLogLevel,
  ServiceWorkerFailureKind,
  toServiceWorkerDiagnosticsLog,
} from './model';

/** Triggers can carry dynamic values (e.g. a spinner reason), so cap them too. */
const MAX_REPORTS_PER_PAGE_LOAD = 5;
const reportedTriggers = new Set<string>();
/** Kinds logged once per page load, by level: one seen offline does not hide the same failure online. */
const reportedOnceFailures = new Set<string>();
let lastFailureKind: ServiceWorkerFailureKind | null = null;

type ReportDetails = Record<string, unknown> & {
  acknowledged?: boolean | null;
  /** The network state the failed request saw, when it was a request. */
  online?: boolean;
};

function isPageOnline({ online }: ReportDetails): boolean {
  return online !== false && navigator.onLine !== false;
}

function reportedOnceKey(failureKind: ServiceWorkerFailureKind, level: LogLevel): string {
  return `${failureKind}:${level}`;
}

/** Whether this failure was logged at this level already on this page, for a kind one log tells all of. */
function isAlreadyReported(failureKind: ServiceWorkerFailureKind, level: LogLevel): boolean {
  return isFailureKindLoggedOnce(failureKind) && reportedOnceFailures.has(reportedOnceKey(failureKind, level));
}

function markReported(failureKind: ServiceWorkerFailureKind, level: LogLevel): void {
  if (isFailureKindLoggedOnce(failureKind)) {
    reportedOnceFailures.add(reportedOnceKey(failureKind, level));
  }
}

/**
 * Logs why the service worker did not answer, with a snapshot of its state.
 * The message names the failure kind, so Sentry groups by cause; `trigger`
 * (an operation or spinner reason) and `requestId` go with the details.
 * Once per trigger per page load (once per kind for a kind one cause shows on
 * every channel): the snapshot reads every IndexedDB entity and the Sentry
 * quota is shared by the whole app. Without network it is info.
 */
export function reportUnresponsiveServiceWorker(
  trigger: string,
  details: ReportDetails = {}
): void {
  if (
    reportedTriggers.has(trigger) ||
    reportedTriggers.size >= MAX_REPORTS_PER_PAGE_LOAD
  ) {
    return;
  }
  reportedTriggers.add(trigger);
  void collectServiceWorkerDiagnostics().then((diagnostics) => {
    const failureKind = classifyServiceWorkerFailure(diagnostics, details.acknowledged ?? null);
    lastFailureKind = failureKind;
    const level = serviceWorkerFailureLogLevel(failureKind, isPageOnline(details));
    if (isAlreadyReported(failureKind, level)) {
      return;
    }
    markReported(failureKind, level);
    const log = level === LogLevel.INFO ? logInfo : logError;
    log(`Service worker did not respond: ${failureKind}`, {
      trigger,
      failureKind,
      ...details,
      ...toServiceWorkerDiagnosticsLog(diagnostics),
    });
    if (isOutdatedWorkerFailure(failureKind)) {
      requestServiceWorkerUpdate(trigger);
    }
  });
}

/** Cause found by the latest report on this page, e.g. for a spinner that keeps spinning. */
export function getLastServiceWorkerFailureKind(): ServiceWorkerFailureKind | null {
  return lastFailureKind;
}
